import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Input,
  InputGroup,
  InputLeftAddon,
  useToast,
  FormControl,
  FormLabel,
  Flex,
  Checkbox,
  Select,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../api/axios";
import { User } from "../../../../utils/types/user";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/reducer/rootReducer";
import { AppDispatch } from "../../../../redux/store";
import { createUserFormik } from "../../../../utils/formik/signup";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import FormInput from "../../../../components/input";
import { color } from "../../../../utils/theme";

interface NewUserFormProps {
  isOpen: boolean;
  onClose: () => void;
  reloadTable: () => void;
  isEdit?: boolean;
  editUser?: User | null;
}

function CreateUserModal({
  isOpen,
  onClose,
  reloadTable,
  isEdit = false,
  editUser = null,
}: NewUserFormProps) {
  const dispatch: AppDispatch = useDispatch();
  const [roles, setRoles] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const user: User | null = useSelector((state: RootState) => state.user.user);
  const navigate = useNavigate();
  const toast = useToast();
  const formik = useFormik({
    ...createUserFormik,
    onSubmit: async () => {
      setSubmitting(true);
      const payload = {
        first_name: formik.values.firstname || "",
        middle_name: formik.values.middlename || "",
        last_name: formik.values.lastname || "",
        role_id: formik.values.role || "",
        facility_id: user?.facility_id || "",
        email: formik.values.email || "",
        phone_number: formik.values.phonenumber || "",
        password: formik.values.password || "",
      };

      try {
        console.log("Submitting payload:", payload);

        // Send the payload using axiosInstance
        const response = await axiosInstance.post("/user/sponsored", payload);
        setSubmitting(false);

        // Reset form and close modal
        onClose();
        toast({
          title: "User Created.",
          position: "top-right",
          description: "User created successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        reloadTable();
      } catch (error: any) {
        console.log("error", error);
        let title = "Failed";
        let description = "Something went wrong";
        if (error.response.data.code === "USER_ALREADY_REGISTERED") {
          title = "User already registered";
          description =
            "User already registered, please change the email address.";
        }
        toast({
          title: title,
          position: "top-right",
          description: description,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    fetchRoles();
  }, [isEdit, editUser]);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get("/role/forDropdown");
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };
  return (
    <Box
      display={"flex"}
      h={"100%"}
      w={"100%"}
      flexDirection={"column"}
      justifyContent={"flex-start"}
      alignContent={"center"}
      alignItems={"center"}
      textAlign={"left"}
    >
      <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction={"column"} gap={4}>
              <Flex direction={"row"} width={"100%"} gap={4}>
                <FormInput
                  name="firstname"
                  isRequired={true}
                  value={formik.values.firstname}
                  label="First Name"
                  type="text"
                  id="First Name"
                  errorMessage={formik.errors.firstname as string}
                  onChange={(event) => {
                    formik.setFieldValue("firstname", event.target.value);
                  }}
                />

                <FormInput
                  id="middlename"
                  isRequired={true}
                  value={formik.values.middlename}
                  name="middlename"
                  label="Middle Name"
                  type={"text"}
                  errorMessage={formik.errors.middlename as string}
                  onChange={(event) => {
                    formik.setFieldValue("middlename", event.target.value);
                  }}
                />
              </Flex>
              <Flex direction={"row"} width={"100%"} gap={4}>
                <FormInput
                  id="lastname"
                  isRequired={true}
                  value={formik.values.lastname}
                  name="lastname"
                  label="Last Name"
                  type={"text"}
                  errorMessage={formik.errors.lastname as string}
                  onChange={(event) => {
                    formik.setFieldValue("lastname", event.target.value);
                  }}
                />
                <FormControl style={{ width: "100%" }}>
                  <FormLabel
                    display={"flex"}
                    mb={0}
                    fontWeight={"500"}
                    color={color.text}
                  >
                    Role
                    <Text color={"red"} mx={1} fontSize={24}>
                      *
                    </Text>
                  </FormLabel>
                  <Select
                    id={"sex"}
                    value={formik.values.role}
                    onChange={(event) =>
                      formik.setFieldValue("role", event.target.value)
                    }
                  >
                    {roles.map((role: any) => (
                      <option key={role?.id} value={role?.id}>
                        {role?.name}
                      </option>
                    ))}
                  </Select>
                  <Text
                    marginY={3}
                    fontWeight={"normal"}
                    color={"red"}
                    fontSize={13}
                  >
                    {formik.errors.role as string}
                  </Text>
                </FormControl>
              </Flex>
              <Flex direction={"row"} width={"100%"} gap={4}>
                <FormControl>
                  <FormLabel
                    display={"flex"}
                    mb={0}
                    fontWeight={"500"}
                    color={color.text}
                  >
                    Phone Number
                    <Text color={"red"} mx={1} fontSize={24}>
                      *
                    </Text>
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon>+251</InputLeftAddon>
                    <Input
                      type="number"
                      fontFamily={"body"}
                      onChange={(event) =>
                        formik.setFieldValue("phonenumber", event.target.value)
                      }
                      inputMode="numeric"
                      placeholder="phone number"
                    />
                  </InputGroup>
                  <Text
                    marginY={3}
                    fontWeight={"normal"}
                    color={"red"}
                    fontSize={13}
                  >
                    {formik.errors.phonenumber as string}
                  </Text>
                </FormControl>
                <FormInput
                  id="email"
                  isRequired={true}
                  value={formik.values.email}
                  name="email"
                  label="Email"
                  type={"email"}
                  errorMessage={formik.errors.email as string}
                  onChange={(event) => {
                    formik.setFieldValue("email", event.target.value);
                  }}
                />
              </Flex>

              <Flex direction={"row"} gap={5} width={"100%"}>
                <FormInput
                  id="password"
                  isRequired={true}
                  value={formik.values.password}
                  name="password"
                  label="Create Password"
                  type={showPassword ? "text" : "password"}
                  errorMessage={formik.errors.password as string}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    formik.setFieldValue("password", event.target.value);
                  }}
                />
                <FormInput
                  id="confirmpassword"
                  isRequired={true}
                  value={confirmPassword}
                  name="confirmpassword"
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  onChange={(event) => {
                    //   formik.setFieldValue("position", event.target.value);
                    setConfirmPassword(event.target.value);
                  }}
                />
              </Flex>
            </Flex>
            <Checkbox
              size="md"
              colorScheme="blue"
              mt={4}
              fontWeight={"500"}
              w={"100%"}
              onChange={(e) => setShowPassword(e.target.checked)}
            >
              Show Password
            </Checkbox>
            {showError && (
              <Text
                marginY={3}
                fontWeight={"normal"}
                color={"red"}
                fontSize={13}
              >
                Passwords do not match
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              w={"100%"}
              isLoading={submitting}
              mr={3}
              onClick={() => {
                if (password.toString() !== confirmPassword.toString()) {
                  setShowError(true);
                  return;
                } else {
                  setShowError(false);
                }
                console.log("formik.errors: ", formik.errors);

                formik.handleSubmit();
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default CreateUserModal;
